import {
  LocalSearchMsg,
  useSetSearchMsgSelectedSendTime,
} from 'hooks/business/searchMsgSelectedMsg';
import { HTMLAttributes, ReactNode, useEffect, useRef } from 'react';
import { Avatar, Button } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import type { DialogueRefObject } from 'components/Dialogue/types';
import { useTranslate } from '@tolgee/react';
import { useGetAllSticker, useStickers } from 'hooks/business/im/sticker';
import { buyerNickMask } from 'services/channels/demo';
import { getImageThumb } from 'lib/utils/platformImage';
import { ChatMain } from 'views/Conversation/Chat/components/ChatMain';
import {
  ChatConversationProvider,
  useChatConversationContext,
} from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { useQuery } from 'components/RouterQuery';
import { scheduleIdle } from 'utils/scheduleIdle';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 300px;
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 40px;
    background: #fff;
    border-bottom: solid 1px var(--theme-border-1);
    > div > span {
      margin: 0 8px;
    }
  }
  > div {
    flex: 1 0 0;
  }
`;

export interface GalaxyImplementProps extends HTMLAttributes<HTMLDivElement> {
  visible?: boolean;
  conversationId: string;
  searchMessage?: LocalSearchMsg;
  targetMessageExtra?: ReactNode;
  extraRender?: (props: { dialogueRef?: DialogueRefObject | null }) => ReactNode;
  customHeader?: ReactNode;
  iframeMode?: boolean;
  galaxyModule?: Conversation.GalaxyModule;
  highlightMsg?: Record<string, string>;
}

const LocateQA = (props: { dialogueRef: DialogueRefObject | null | undefined }) => {
  const { dialogueRef } = props;
  const { locateMsgId } = useQuery();

  return locateMsgId ? (
    <Button
      type="link"
      onClick={() => {
        if (dialogueRef && locateMsgId) {
          dialogueRef.scrollToMessageByScrollTo(locateMsgId, {
            behavior: 'smooth',
          });
        }
      }}
    >
      定位到Q&A位置
    </Button>
  ) : null;
};

function GalaxyImplement(props: GalaxyImplementProps) {
  const {
    conversationId,
    searchMessage,
    targetMessageExtra,
    extraRender = ({ dialogueRef }) => {
      if (props.galaxyModule === 'productKnowledge') {
        return <LocateQA dialogueRef={dialogueRef} />;
      }

      return searchMessage ? (
        <Button
          type="link"
          onClick={() => {
            if (dialogueRef) {
              dialogueRef.scrollToMessageByScrollTo(searchMessage.msgId, {
                behavior: 'smooth',
              });
            }
          }}
        >
          {t('feedback.定位至反馈内容')}
        </Button>
      ) : null;
    },
    iframeMode = false,
    galaxyModule,
    customHeader,
    highlightMsg,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const { currentConversation: conversation } = useChatConversationContext();

  const setSearchMessage = useSetSearchMsgSelectedSendTime();

  const dialogueRef = useRef<DialogueRefObject | null>();

  const { channel, region } = conversation || {};

  const stickers = useStickers({
    region: region || 'ID',
    channel: channel || 'SHOPEE',
  });

  const getAllSticker = useGetAllSticker();

  const refsCurrent = {
    setSearchMessage,
    getAllSticker,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const { sendTime = '', msgId = '' } = searchMessage || {};

  useEffect(() => {
    refs.current.setSearchMessage({
      sendTime,
      msgId,
    });

    return () => {
      refs.current.setSearchMessage({
        sendTime: '',
        msgId: '',
      });
    };
  }, [sendTime, msgId]);

  useEffect(() => {
    if (stickers || !channel || !region || channel === 'WA_BIZ') {
      return;
    }

    scheduleIdle(() => {
      refs.current.getAllSticker();
    });
  }, [channel, region, stickers]);

  if (!conversation) {
    return null;
  }

  const { buyerAvatar, buyerNick: buyerNickRaw } = conversation;
  const buyerNick = buyerNickMask(buyerNickRaw);
  return (
    <Container {...otherProps}>
      {customHeader ? (
        customHeader
      ) : (
        <header>
          <div>
            {buyerAvatar ? (
              <Avatar shape="square" size={24} src={getImageThumb(buyerAvatar, channel)} />
            ) : (
              <Avatar shape="square" size={24} icon={<UserOutlined />} />
            )}
            <span>{buyerNick}</span>
          </div>
          {extraRender({ dialogueRef: dialogueRef.current })}
        </header>
      )}
      <ChatMain
        conversationId={conversationId}
        key={conversationId}
        isGalaxy
        iframeMode={iframeMode}
        galaxyModule={galaxyModule}
        targetMessageExtra={targetMessageExtra}
        getDialogueRef={(ref) => {
          dialogueRef.current = ref;
        }}
        preventPosScroll
        highlightMsg={highlightMsg}
      />
    </Container>
  );
}

const GalaxyImplementEntry = (props: GalaxyImplementProps) => {
  return (
    <ChatConversationProvider conversationId={props.conversationId}>
      <GalaxyImplement {...props} />
    </ChatConversationProvider>
  );
};

export default GalaxyImplementEntry;
